import { Box, Grid, GridItem, Spinner } from '@chakra-ui/react'
import { ModalBuild, NotExist } from 'components/molecules'
import { Bar } from 'components/molecules/Bar'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { useAuthContext } from 'contexts/auth'
import { useInstitutionsContext } from 'contexts/institutions'
import { motion } from 'framer-motion'

import { InstitutionsQueryDTO, InstitutionsResponseDTO } from 'models'
import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import formatter from 'utils/formatter'
import { useDebounce } from 'utils/useDebounce'
import Card from './components/Card'

import LimitPagination from './components/LimitPagination'
import Steps from './components/Steps'

const Institutions: React.FC = () => {
    const [filter, setFilter] = useState('')
    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState<number>(10)
    const [totalPages, setTotalPages] = useState<number>(0)
    const [loading, setLoading] = useState(true)

    const { isBackoffice } = useAuthContext()

    const labelInstitutions = isBackoffice ? 'Instituições' : 'Instituição'

    const { loadingInstitution, getInstitutions } = useInstitutionsContext()
    const [institutions, setInstitution] = useState<InstitutionsResponseDTO[]>()

    const handleLimit = (value: number) => {
        setLoading(true)
        setLimit(value)
    }

    const handlePage = (value: number) => {
        setPage(value)
    }

    const addModalRef = useRef<ModalBuildHandle>(null)

    const handleAddModalOpen = (): void => {
        addModalRef.current?.onOpen()
    }

    const handleAddModalClose = (): void => {
        addModalRef.current?.onClose()
    }

    const handleSubmit = () => {
        handleAddModalClose()
        handleFilter(
            filter
                ? {
                      razaoSocial: filter,
                      cnpj: filter.replace(/\D/g, '')
                  }
                : {}
        )
    }

    const handleSearch = useDebounce((e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toLowerCase()
        setFilter(value)
    })

    const handleFilter = useDebounce((query?: InstitutionsQueryDTO) => {
        getInstitutions({
            ...query,
            pagina: page ?? 1,
            limite: limit
        })
            .then(res => {
                setTotalPages(+res.headers['total-pages'])
                setInstitution(res.data)
                setLimit(limit)
            })
            .finally(() => setLoading(false))
    })

    const Loading = () => {
        return (
            <Box
                as={motion.div}
                bg="white"
                borderTopRadius="8px"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                height="100%"
                position="relative"
            >
                <Spinner
                    size="xl"
                    speed="1s"
                    position="absolute"
                    top="calc(50% - 33px)"
                    left="calc(50% - 33px)"
                />
            </Box>
        )
    }

    useEffect(() => {
        handleFilter(
            filter
                ? {
                      razaoSocial: filter,
                      cnpj: filter.replace(/\D/g, '')
                  }
                : {}
        )
    }, [filter, page, limit])

    return (
        <Box
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            height="100%"
            display="flex"
            flexDir="column"
            pb="33px"
        >
            <Bar
                title={labelInstitutions}
                input={{
                    props: {
                        w: '100%',
                        variant: 'flushed',
                        placeholder: 'Buscar pelo nome da instituição ou CNPJ'
                    }
                }}
                action={
                    isBackoffice
                        ? {
                              text: 'Nova instituição',
                              props: {
                                  colorScheme: 'primary'
                              }
                          }
                        : undefined
                }
                onChangeInput={handleSearch}
                onClickActions={handleAddModalOpen}
            />
            {loadingInstitution ? (
                <Loading />
            ) : institutions?.length ? (
                <>
                    <Box
                        as={motion.div}
                        data-testid="table-box"
                        bgColor="white"
                        p="33px"
                        pb="14px"
                        borderTopRadius="8px"
                        justifyContent="center"
                        overflowY="scroll"
                        height="100%"
                    >
                        <Grid
                            data-testid="institutions"
                            templateColumns="repeat(auto-fill, minmax(300px, 1fr))"
                            columnGap="46px"
                            rowGap="32px"
                        >
                            {institutions?.map((institution, key) => (
                                <GridItem key={key} w="100%" minW="300px">
                                    <Card
                                        active={!!institution?.ativa}
                                        href={`/instituicoes/${institution.id}`}
                                        title={institution.razaoSocial}
                                        idInstituition={institution.id}
                                        header={{
                                            cnpj: institution.cnpj
                                                ? formatter.cnpj(
                                                      institution.cnpj
                                                  )
                                                : '',
                                            status: true
                                        }}
                                    />
                                </GridItem>
                            ))}
                        </Grid>
                    </Box>
                </>
            ) : (
                <NotExist
                    action={{
                        text: 'institution',
                        onClick: () => handleAddModalOpen()
                    }}
                    isBackoffice={isBackoffice}
                    paddingBlock={20}
                    height="100%"
                />
            )}

            {institutions?.length ? (
                <LimitPagination
                    pagination={{
                        page,
                        limit,
                        totalPages
                    }}
                    onChangePage={handlePage}
                    onChangeLimit={value => handleLimit(value)}
                    loading={{
                        status: loading
                    }}
                />
            ) : (
                <></>
            )}

            <ModalBuild
                ref={addModalRef}
                closeOnOverlayClick={false}
                size="4xl"
                shut="white"
            >
                <Steps onSubmit={handleSubmit} onCancel={handleAddModalClose} />
            </ModalBuild>
        </Box>
    )
}

export default Institutions
